import { useRef } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useModal } from 'context/ModalContext';
import { getButtonsCancelOk } from 'context/helpers/helperModalContext';
import spinnerTitle from 'components/others/spinnerTitle';

const EMAIL_BODY_MAX_LENGTH = 1000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export interface SendGenerationEmailResult {
    email: string;
    emailBody: string;
}

export interface SendGenerationEmailDialogLabels {
    title?: string;
    emailLabel?: string;
    emailPlaceholder?: string;
    emailHint?: string;
    emailRequired?: string;
    emailInvalid?: (emails: string) => string;
    bodyLabel?: string;
    bodyPlaceholder?: string;
    submit?: string;
    submitting?: string;
}

interface OpenSendGenerationEmailParams {
    defaultEmail?: string;
    onSubmit: (result: SendGenerationEmailResult) => Promise<boolean> | boolean;
    labels?: SendGenerationEmailDialogLabels;
}

interface DlgButton {
    title: ReactNode;
    variant?: string;
    disabled?: boolean;
    busy?: boolean;
    handleClick?: () => void;
}

const parseEmailList = (value: string): string[] =>
    String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

const normalizeEmailList = (emails: string[]): string => emails.join(', ');

const useSendGenerationEmailDialog = () => {
    const { t } = useTranslation(['Proposal', 'common']);
    const { showDlg, setDlgButtons } = useModal();
    const refForm = useRef<HTMLFormElement | null>(null);
    const refEmail = useRef<HTMLInputElement | null>(null);
    const refBody = useRef<HTMLTextAreaElement | null>(null);
    const refEmailError = useRef<HTMLDivElement | null>(null);
    const submittingRef = useRef(false);

    const setEmailError = (message: string) => {
        if (refEmailError.current) {
            refEmailError.current.textContent = message;
            refEmailError.current.classList.toggle('d-none', !message);
        }
        if (refEmail.current) {
            refEmail.current.classList.toggle('is-invalid', Boolean(message));
        }
    };

    const openSendGenerationEmail = ({
        defaultEmail = '',
        onSubmit,
        labels,
    }: OpenSendGenerationEmailParams) => {
        submittingRef.current = false;

        const submitLabel = labels?.submit || t('edit.generate.sendDialog.submit');
        const submittingLabel = labels?.submitting || t('edit.generate.sendDialog.submitting');

        const buildButtons = (submitting: boolean): DlgButton[] => {
            const buttons: DlgButton[] = getButtonsCancelOk(t).map((button) => ({
                ...button,
                disabled: submitting,
                busy: false,
                handleClick: undefined,
            }));

            buttons[0].handleClick = () => {
                if (submittingRef.current) return;
                showDlg(false);
            };
            buttons[1].title = spinnerTitle(submitLabel, submitting, submittingLabel);
            buttons[1].busy = submitting;
            buttons[1].handleClick = () => {
                void handleSubmit();
            };
            return buttons;
        };

        const handleSubmit = async (e?: FormEvent) => {
            e?.preventDefault();
            if (submittingRef.current) return;

            const rawEmail = String(refEmail.current?.value || '');
            const emails = parseEmailList(rawEmail);
            const emailBody = String(refBody.current?.value || '').trim().slice(0, EMAIL_BODY_MAX_LENGTH);

            if (!emails.length) {
                setEmailError(labels?.emailRequired || t('edit.generate.sendDialog.emailRequired'));
                refEmail.current?.focus();
                return;
            }

            const invalidEmails = emails.filter((email) => !EMAIL_RE.test(email));
            if (invalidEmails.length) {
                const joined = invalidEmails.join(', ');
                setEmailError(
                    labels?.emailInvalid?.(joined) ||
                        t('edit.generate.sendDialog.emailInvalid', {
                            emails: joined,
                        }),
                );
                refEmail.current?.focus();
                return;
            }

            setEmailError('');
            const email = normalizeEmailList(emails);

            submittingRef.current = true;
            setDlgButtons?.(buildButtons(true));
            try {
                const ok = await onSubmit({ email, emailBody });
                if (ok) {
                    showDlg(false);
                    return;
                }
            } finally {
                submittingRef.current = false;
                setDlgButtons?.(buildButtons(false));
            }
        };

        showDlg({
            type: 'confirm',
            title: labels?.title || t('edit.generate.sendDialog.title'),
            buttons: buildButtons(false),
            idFirstFocus: 'generation-send-email-input',
            text: (
                <form ref={refForm} onSubmit={(e) => void handleSubmit(e)} noValidate>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {labels?.emailLabel || t('edit.generate.sendDialog.emailLabel')}
                        </Form.Label>
                        <Form.Control
                            id="generation-send-email-input"
                            ref={refEmail}
                            type="text"
                            defaultValue={defaultEmail}
                            placeholder={
                                labels?.emailPlaceholder ||
                                t('edit.generate.sendDialog.emailPlaceholder')
                            }
                            autoComplete="email"
                            onInput={() => setEmailError('')}
                        />
                        <div className="form-text">
                            {labels?.emailHint || t('edit.generate.sendDialog.emailHint')}
                        </div>
                        <div
                            ref={refEmailError}
                            className="text-danger fs-12 mt-1 d-none"
                            id="generation-send-email-error"
                        />
                    </Form.Group>
                    <Form.Group className="mb-0">
                        <Form.Label>
                            {labels?.bodyLabel || t('edit.generate.sendDialog.bodyLabel')}
                        </Form.Label>
                        <Form.Control
                            ref={refBody}
                            as="textarea"
                            rows={5}
                            maxLength={EMAIL_BODY_MAX_LENGTH}
                            placeholder={
                                labels?.bodyPlaceholder ||
                                t('edit.generate.sendDialog.bodyPlaceholder')
                            }
                            style={{ resize: 'vertical' }}
                            onInput={(e) => {
                                const target = e.currentTarget;
                                const counter = document.getElementById('generation-send-email-body-counter');
                                if (counter) {
                                    counter.textContent = `${String(target.value || '').length} / ${EMAIL_BODY_MAX_LENGTH}`;
                                }
                            }}
                        />
                        <div className="form-text text-end" id="generation-send-email-body-counter">
                            0 / {EMAIL_BODY_MAX_LENGTH}
                        </div>
                    </Form.Group>
                    <button type="submit" className="d-none" />
                </form>
            ),
        });
    };

    return { openSendGenerationEmail };
};

export default useSendGenerationEmailDialog;
export { EMAIL_BODY_MAX_LENGTH };
